import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { parseISO } from "date-fns";
import { ExpandedState } from "@tanstack/react-table";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { useProfitAndLossQuery } from "@api/accountingReports.api";
import { useJeConversionInProgress } from "@hooks/useJeConversionInProgress";
import { AccountingReportType } from "@common/types/reports/report-type";
import { ReportDataProcessingBanner } from "@components/accounting/ReportDataProcessingBanner";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { AccountingReportFilters } from "../types";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { profitLossFilterDefinition } from "../accountingReportFilterDefinitions";
import { AccountingReportViewToolbar } from "../reportViews/AccountingReportViewToolbar";
import { useAccountingTabContext } from "../useAccountingTab";
import { useConsolidatedGlDrillDown } from "../useConsolidatedGlDrillDown";
import { useConsolidatedReportView } from "../useConsolidatedReportView";
import { useProfitLossDownload } from "./useProfitLossDownload";
import { ProfitLossPreviewContent } from "./ProfitLossPreviewContent";
import { ProfitLossDrillDownParams } from "./profitLossDrillDown.lib";
import {
    buildProfitLossRows,
    getProfitLossDefaultExpandedState,
} from "./profitLossViewBuilder";

// parseISO (not `new Date`) so a date-only string ('2026-01-01') parses to
// local midnight, matching how the rest of this codebase reads report dates
// (see PLTaskGLDrilldownDrawer) and avoiding a day-shift in timezones behind
// UTC.
function toDateOnly(value?: Date | string): Date | undefined {
    if (!value) {
        return undefined;
    }
    return typeof value === "string" ? parseISO(value) : value;
}

export const ProfitLossPreviewPage: React.FC = () => {
    const { filters, setFilters, selectedEntity, ledgerBasis } =
        useAccountingTabContext();
    const [drillDownParams, setDrillDownParams] =
        useState<ProfitLossDrillDownParams | null>(null);
    // GlDrilldownDrawer seeds its internal filters from `initialFilters` only
    // on mount (see GlDrilldownDrawerContent), so clicking a different P&L
    // amount while the drawer is already open would otherwise keep showing
    // the previous drill-down's data. Bumping this key forces a remount on
    // every new drill-down so the drawer always reflects the latest click.
    const [drillDownKey, setDrillDownKey] = useState(0);
    const handleDrillDown = useCallback((params: ProfitLossDrillDownParams) => {
        setDrillDownKey((key) => key + 1);
        setDrillDownParams(params);
    }, []);

    const drillDownFilters = useMemo<AccountingReportFilters | null>(() => {
        if (!drillDownParams) {
            return null;
        }
        return {
            startDate: toDateOnly(drillDownParams.startDate),
            endDate: toDateOnly(drillDownParams.endDate),
            entityId: drillDownParams.entityId,
            accountCodes: drillDownParams.accountCodes,
            classes: drillDownParams.classes,
            classFilterType: drillDownParams.classFilterType,
            counterpartiesIds: drillDownParams.counterpartiesIds,
            withEmptyOrUnverifiedCounterparty:
                drillDownParams.withEmptyOrUnverifiedCounterparty,
            hasCounterparty: drillDownParams.hasCounterparty,
            accountIds: drillDownParams.accountIds,
            subaccountIds: drillDownParams.subaccountIds,
            ledgerId: drillDownParams.ledgerId,
        };
    }, [drillDownParams]);

    const drillDownEntityIds = useMemo(
        () =>
            drillDownParams?.entityId !== undefined
                ? [drillDownParams.entityId]
                : [],
        [drillDownParams],
    );

    // GlDrilldownDrawer plays a close animation while `open` transitions to
    // false, so the last non-null filters/entityIds must stay available for
    // that render (mirrors DirectoryGlDrilldownDrawer's lastGlDrawerStateRef) —
    // otherwise the drawer content would blank out mid-close.
    const lastDrillDownFiltersRef = useRef<AccountingReportFilters | null>(
        null,
    );
    if (drillDownFilters) {
        lastDrillDownFiltersRef.current = drillDownFilters;
    }
    const lastDrillDownEntityIdsRef = useRef<number[]>([]);
    if (drillDownParams) {
        lastDrillDownEntityIdsRef.current = drillDownEntityIds;
    }
    const { consolidationGroup, isConsolidated } = useConsolidatedReportView({
        filters,
        setFilters,
    });
    const toGlDrillDownTarget = useConsolidatedGlDrillDown({
        consolidationGroup,
        isConsolidated,
        ledgerBasis,
    });
    const glDrillDownTarget = lastDrillDownFiltersRef.current
        ? toGlDrillDownTarget({
              filters: lastDrillDownFiltersRef.current,
              entityIds: lastDrillDownEntityIdsRef.current,
          })
        : null;
    const filtersWithDefaultValues = useMemo(() => {
        if (filters.cycle === undefined) {
            return {
                ...filters,
                cycle: profitLossFilterDefinition.defaultCycle,
            };
        }
        return filters;
    }, [filters]);

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_PROFIT_LOSS_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const { isJeConversionInProgress } = useJeConversionInProgress(
        selectedEntity?.id,
    );

    const { profitAndLoss, generationDate } = useProfitAndLossQuery(
        filtersWithDefaultValues,
        {
            includeCounterpartyBreakdown: true,
            enabled: true,
        },
    );

    const { isDownloading, download } = useProfitLossDownload(
        filtersWithDefaultValues,
        profitAndLoss.data?.presentation?.rowVisibility,
    );

    const rows = useMemo(
        () =>
            profitAndLoss.data ? buildProfitLossRows(profitAndLoss.data) : [],
        [profitAndLoss.data],
    );
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);
    useEffect(() => {
        if (rows.length > 0) {
            setExpanded(getProfitLossDefaultExpandedState(rows));
        }
    }, [rows]);

    return (
        <>
            {isJeConversionInProgress && <ReportDataProcessingBanner />}
            <AccountingReportViewToolbar
                reportId="profitLoss"
                filters={filtersWithDefaultValues}
                setFilters={setFilters}
            >
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
                <DownloadDropdown
                    onDownload={(format) =>
                        selectedEntity &&
                        download({
                            entity: selectedEntity,
                            format,
                            startDate: filtersWithDefaultValues.startDate,
                            endDate: filtersWithDefaultValues.endDate,
                        })
                    }
                    loading={isDownloading}
                    disabled={
                        profitAndLoss.isLoading || !profitAndLoss.isSuccess
                    }
                />
            </AccountingReportViewToolbar>
            <AccountingReportFiltersForm
                {...profitLossFilterDefinition.filterProps}
                showFinancialAccountFilter={!isConsolidated}
                filters={filtersWithDefaultValues}
                setFilters={setFilters}
                consolidationGroup={consolidationGroup}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                shouldTrackDatePreset
                hasEntityGrouping
                hasClassGrouping
                requireDateSelection
                hideSearch
                hideLedgerSelect={isConsolidated}
                showCyclesSelect={!isConsolidated}
                reportType={AccountingReportType.PROFIT_LOSS}
            />
            <LoaderOnInitialLoading
                queryResult={profitAndLoss}
                dimWhileFetching
            >
                {profitAndLoss.data && selectedEntity && (
                    <ProfitLossPreviewContent
                        profitLoss={profitAndLoss.data}
                        filters={filtersWithDefaultValues}
                        chrome={profitAndLoss.data.presentation.chrome}
                        numberFormat={
                            profitAndLoss.data.presentation.numberFormat
                        }
                        generationDate={generationDate}
                        entity={selectedEntity}
                        rows={rows}
                        expanded={expanded}
                        onExpandedChange={setExpanded}
                        tableRef={tableRef}
                        onDrillDown={handleDrillDown}
                    />
                )}
            </LoaderOnInitialLoading>
            {glDrillDownTarget && (
                <GlDrilldownDrawer
                    key={drillDownKey}
                    open={drillDownParams !== null}
                    onClose={() => setDrillDownParams(null)}
                    filters={glDrillDownTarget.filters}
                    entityIds={lastDrillDownEntityIdsRef.current}
                    ledgerBasis={glDrillDownTarget.ledgerBasis}
                />
            )}
        </>
    );
};
